import { Base64File } from '@synergy/models';
import { Locale } from '@synergy/models';
import { createSelector } from 'reselect';
import { errorsToTouch } from '@synergy/utils';

import { AppState } from '../../../../../../store/models/app-state';
import { SupplierSpecialOffer } from '../../../models/supplier-special-offer';
import { SupplierSpecialOffersDetailOverviewRequest } from '../models/supplier-special-offers-detail-overview-request';
import { SupplierSpecialOffersOverviewFormValues } from '../models/supplier-special-offers-overview-form-values';
import { selectedLanguageSelector } from '../../../../../../common/selectors/selected-language-selectors';

const supplierSpecialOffersDetailOverviewState = (state: AppState) =>
    state.contentManagement.supplierSpecialOffers.detail.overview;

/**
 * The shape the mask edits in: the picture reduced to the one size it shows,
 * the texts picked for the chosen language, and a default for everything the
 * server left empty.
 *
 * Shared with the unsaved-changes baseline (#2159) — compared against raw
 * server values, all of that would read as something the user typed.
 */
const withDefaults = (
    offer: Partial<SupplierSpecialOffer> | undefined,
    selectedLanguage: Locale
): SupplierSpecialOffersOverviewFormValues => {
    const formValues: SupplierSpecialOffersOverviewFormValues = {
        ...offer,
        image: offer?.image ? { name: '', contentBase64: '', pathname: offer.image['489x274'] } : null,
        supplier: offer?.supplier ?? null,
        validFrom: offer?.validFrom ?? null,
        validTo: offer?.validTo ?? null,
        subtitle: offer?.subtitle?.[selectedLanguage] ?? '',
        title: offer?.title?.[selectedLanguage] ?? '',
        path: offer?.path ?? '',
    };

    return formValues;
};

export const supplierSpecialOffersDetailOverviewFormDataSelector = createSelector(
    [selectedLanguageSelector, supplierSpecialOffersDetailOverviewState],
    (selectedLanguage, state) => ({
        formValues: withDefaults(state.supplierSpecialOffer, selectedLanguage),
        errors: state.overviewErrors,
        touched: errorsToTouch(state.overviewErrors),
        selectedLanguage,
    })
);

/** What the mask compares against to decide whether anything is unsaved. */
export const supplierSpecialOffersDetailOverviewBaselineSelector = createSelector(
    [selectedLanguageSelector, supplierSpecialOffersDetailOverviewState],
    (selectedLanguage, state) => (state.baseline ? withDefaults(state.baseline, selectedLanguage) : undefined)
);

export const supplierSpecialOffersDetailRequestSelector = createSelector(
    supplierSpecialOffersDetailOverviewState,
    state => {
        const specialOffer = state.supplierSpecialOffer;
        if (
            !specialOffer ||
            !specialOffer.subtitle ||
            !specialOffer.title ||
            !specialOffer.supplier ||
            !specialOffer.path ||
            !specialOffer.validFrom ||
            !specialOffer.validTo
        ) {
            return null;
        }

        const request: SupplierSpecialOffersDetailOverviewRequest = {
            id: specialOffer.id,
            image: (specialOffer.image as Base64File)?.contentBase64 ? (specialOffer.image as Base64File) : undefined,
            subtitle: specialOffer.subtitle,
            title: specialOffer.title,
            supplierId: specialOffer.supplier.id,
            path: specialOffer.path,
            validFrom: specialOffer.validFrom,
            validTo: specialOffer.validTo,
        };

        return request;
    }
);
